import { Architecture } from '@mui/icons-material';
import { Box, ClickAwayListener, Modal } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { EActiveDrawer, EInteractionMode, ESubMenu, EToolbarButton } from '../../../../@types/Enums';
import { IconAttachmentPin, IconDrawLayout } from '../../../../assets/icons';
import { secondaryIconActive, secondaryIconHover, secondaryIconInactive } from '../../../../utils/colors';
import { isMUIIcon } from '../../../../utils/iconUtils';
import ImsIcon from '../../ImsIcon';

interface RoomCreationModalProps {
  open: boolean;
  onClose: () => void;
  sizes: any;
  handleToolbarButtonPress: (buttonId: EToolbarButton, subMenuType?: ESubMenu, activatedDrawer?: EActiveDrawer) => void;
  isDrawingAllowed: boolean;
  activeDrawer: EActiveDrawer;
  interactionMode: EInteractionMode;
  setActiveToolbarButton: (button: EToolbarButton | null) => void;
  buttonPosition?: DOMRect;
}

const menuIconStyle = {
  width: '2.25rem',
  height: '2.25rem',
  margin: '.1rem 1rem',
  transition: '.2s ease-in-out',
};

const RoomCreationModal: React.FC<RoomCreationModalProps> = ({
  open,
  onClose,
  handleToolbarButtonPress,
  isDrawingAllowed,
  activeDrawer,
  setActiveToolbarButton,
  interactionMode,
  sizes,
  buttonPosition,
}) => {
  const intl = useIntl();

  // Mark the "Draw" option as disabled if the room is completed
  const options = [
    {
      id: EToolbarButton.DRAWING,
      text: intl.formatMessage({ id: 'Toolbar.draw' }),
      icon: IconDrawLayout,
      disabled: false,
      active: interactionMode === EInteractionMode.DRAW,
      action: () => {
        // Just trigger the toolbar handler. 
        // The Toolbar logic (above) will determine if it needs to turn ON or OFF.
        handleToolbarButtonPress(EToolbarButton.DRAWING);
        
        // Keep the parent button visually active
        setActiveToolbarButton(EToolbarButton.ROOM_CREATION);
        
        onClose();
      },
    },
    {
      id: EToolbarButton.ARCHITECTURE,
      text: intl.formatMessage({ id: 'Toolbar.architecture' }),
      icon: Architecture,
      disabled: false,
      action: () => {
        handleToolbarButtonPress(EToolbarButton.ARCHITECTURE, ESubMenu.ARCHITECTURE, EActiveDrawer.ARCHITECTURE);
        setActiveToolbarButton(EToolbarButton.ROOM_CREATION);
        onClose();
        setTimeout(() => {
          handleToolbarButtonPress(EToolbarButton.ARCHITECTURE, ESubMenu.ARCHITECTURE, EActiveDrawer.ARCHITECTURE);
        }, 0);
      },
    },
    {
      id: EToolbarButton.ATTACHMENT,
      text: intl.formatMessage({ id: 'Toolbar.attachment' }),
      icon: IconAttachmentPin,
      disabled: false,
      action: () => {
        handleToolbarButtonPress(EToolbarButton.ATTACHMENT, ESubMenu.NONE, EActiveDrawer.NONE);
        setActiveToolbarButton(EToolbarButton.ROOM_CREATION);
        onClose();
      },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 999,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 48}px` : '50%',
        width: 'fit-content',
        transform: 'none',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), ' + '0px 4px 5px 0px rgba(0,0,0,0.14), ' + '0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Title Section */}
            <Box
              sx={{
                padding: '0.5rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.5rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.createRoom' })}
              </Box>
            </Box>

            {/* options List */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {options.map((option) => {
                // Determine if this option is active
                const isActive =
                  (option.id === EToolbarButton.DRAWING && isDrawingAllowed) ||
                  (option.id === EToolbarButton.ARCHITECTURE && activeDrawer === EActiveDrawer.ARCHITECTURE) ||
                  (option.id === EToolbarButton.ATTACHMENT && interactionMode === EInteractionMode.ATTACHMENT);

                return (
                  <Box
                    component="div"
                    key={option.id}
                    onClick={!option.disabled ? option.action : undefined}
                    sx={{
                      display: 'grid',
                      gridTemplateRows: '2rem',
                      gridTemplateColumns: '28% 65%',
                      alignItems: 'center',
                      padding: '0.2rem 0.5rem',
                      fontSize: '1rem',
                      borderRadius: 0,
                      cursor: option.disabled ? 'default' : 'pointer',
                      pointerEvents: option.disabled ? 'none' : 'auto',
                      backgroundColor: 'inherit',
                      color: option.disabled ? 'rgba(0, 0, 0, 0.1)' : isActive ? secondaryIconActive : secondaryIconInactive,
                      width: '14rem',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: option.disabled ? 'inherit' : secondaryIconHover,
                      },
                      transition: 'background-color 0.2s ease-in-out',
                    }}
                  >
                    {isMUIIcon(option.icon) ? <option.icon style={menuIconStyle} /> : <ImsIcon Icon={option.icon} style={menuIconStyle} />}
                    {option.text}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default RoomCreationModal;
